import React, { useContext, useMemo, useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MedicationIcon from "@mui/icons-material/Medication";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  gridClasses,
  gridPageCountSelector,
  gridPaginationModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { UserContext } from "../../hooks/userContext";
import { useQueryWantedItems } from "../../queries/wantedItem/hooks/useQueryWantedItems";
import { useMutationDeleteWantedItem } from "../../queries/wantedItem/hooks/useMutationDeleteWantedItem";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../navigation/routeConfig";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";
import axios from "axios";

const WishlistGridPagination = () => {
  const apiRef = useGridApiContext();
  const theme = useTheme();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const totalPages = Math.max(pageCount, 1);
  const currentPage = paginationModel.page + 1;
  const windowStart = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
  const windowEnd = Math.min(totalPages, windowStart + 2);
  const visiblePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, index) => windowStart + index);
  const shouldShowLastPage = totalPages > 3 && windowEnd < totalPages;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        px: 2,
        py: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Page {currentPage} of {totalPages}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small" onClick={() => apiRef.current.setPage(0)} disabled={isFirstPage}>
          <FirstPageRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => apiRef.current.setPage(currentPage - 2)} disabled={isFirstPage}>
          <KeyboardArrowLeftRoundedIcon fontSize="small" />
        </IconButton>

        {visiblePages.map((page) => {
          const isSelected = currentPage === page;
          return (
            <Button
              key={page}
              size="small"
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(page - 1)}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: "8px",
                fontWeight: 600,
                p: 0,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderColor: alpha(theme.palette.primary.main, 0.35),
                ...(isSelected && {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }),
              }}
            >
              {page}
            </Button>
          );
        })}
        {shouldShowLastPage && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
              ...
            </Typography>
            <Button
              size="small"
              variant={currentPage === totalPages ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(totalPages - 1)}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: "8px",
                fontWeight: 600,
                p: 0,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderColor: alpha(theme.palette.primary.main, 0.35),
                ...(currentPage === totalPages && {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }),
              }}
            >
              {totalPages}
            </Button>
          </>
        )}

        <IconButton size="small" onClick={() => apiRef.current.setPage(currentPage)} disabled={isLastPage}>
          <KeyboardArrowRightRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => apiRef.current.setPage(totalPages - 1)} disabled={isLastPage}>
          <LastPageRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const Wishlist: React.FunctionComponent = () => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const navigate = useNavigate();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const { data, loading: queryLoading, refetch } = useQueryWantedItems({
    variables: {
      where: {
        buyerUserId: +id,
      },
    },
    fetchPolicy: "no-cache",
    pollInterval: 5000,
  });

  const [deleteWantedItem, { loading: deleteLoading }] = useMutationDeleteWantedItem({
    onCompleted: () => {
      addToast({ type: NotificationType.Success, title: "Wishlist item deleted." });
      refetch();
    },
    onError: (error: any) => {
      addToast({ type: NotificationType.Error, title: error.message });
    },
  });

  const handleDelete = (wantedItemId: number) => {
    deleteWantedItem({
      variables: {
        where: {
          id: wantedItemId,
        },
      },
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmId) return;
    handleDelete(deleteConfirmId);
    setDeleteConfirmId(null);
  };

  const rows = useMemo(
    () =>
      (data?.findManyWantedItem || []).map((item: any) => ({
        ...item,
        createdAtFormatted: moment(item.createdAt).format("MM/DD/YYYY"),
      })),
    [data?.findManyWantedItem]
  );

  const DrugCellRenderer = ({ params }: { params: GridRenderCellParams<any> }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    React.useEffect(() => {
      const ndcValue = params?.row?.ndcNumber;
      if (!ndcValue) return;

      const formattedNdc = ndcValue.replace(/-/g, "");
      setIsLoading(true);
      axios
        .get(`${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`)
        .then((response) => {
          setImageUrl(response?.data?.imageUrl || null);
        })
        .catch(() => {
          setImageUrl(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, [params?.row?.ndcNumber]);

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {isLoading ? (
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              marginRight: "8px",
              bgcolor: "grey.300",
            }}
          />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt="Drug"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              marginRight: "8px",
            }}
          />
        ) : (
          <MedicationIcon
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              marginRight: "8px",
              color: "#ccc",
            }}
          />
        )}
        <span>{params.value}</span>
      </Box>
    );
  };

  const columns: GridColDef[] = [
    { field: "ndcNumber", headerName: "NDC", minWidth: 170, flex: 1, sortable: true, disableColumnMenu: true },
    {
      field: "drugName",
      headerName: "Drug Name",
      minWidth: 260,
      flex: 1.2,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<any>) => <DrugCellRenderer params={params} />,
    },
    { field: "manufacturer", headerName: "Manufacturer", minWidth: 200, flex: 1, sortable: true, disableColumnMenu: true },
    { field: "dosageForm", headerName: "Dosage Form", minWidth: 150, flex: 1, sortable: true, disableColumnMenu: true },
    { field: "strength", headerName: "Strength", minWidth: 120, flex: 1, sortable: true, disableColumnMenu: true },
    { field: "createdAtFormatted", headerName: "Created", minWidth: 120, flex: 0.8, sortable: true, disableColumnMenu: true },
    {
      field: "action",
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      minWidth: 90,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <IconButton
            aria-label="delete wishlist item"
            onClick={() => setDeleteConfirmId(+params.id)}
            disabled={deleteLoading}
            size="small"
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "row", pb: 3 }}>
        <Typography component="h4" variant="h6">
          Wishlist
        </Typography>
        <Button variant="contained" onClick={() => navigate(routeConfigs.newWishlistItem.path)}>
          Add to wishlist
        </Button>
      </Box>

      <Box sx={{ height: "calc(100vh - 236px)", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={queryLoading}
          slots={{ pagination: WishlistGridPagination }}
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10]}
          sx={{
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
              outline: "none",
            },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
              outline: "none",
            },
            "& .MuiDataGrid-footerContainer": {
              minHeight: 56,
              borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.25),
            },
          }}
        />
      </Box>

      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Wishlist Item</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this item
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
};

export default Wishlist;
